import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getAdobeSession,
  logoutAdobeSession,
  startAdobeLogin,
  type AdobeSessionUser
} from '../api/adobeAuthApi';

const DEFAULT_PARTNER_ORG = '';
const DEFAULT_PARTNER_CONTACT = '';
const DEFAULT_PARTNER_EMAIL = '';

export type VipMpSessionValue = {
  analystRole: 'om' | 'bpops' | 'cam';
  analystName: string;
  analystEmail: string;
  partnerOrgName: string;
  partnerContactName: string;
  partnerContactEmail: string;
  setPartnerProfile: (org: string, name: string, email: string) => void;
  authStatus: 'loading' | 'authenticated' | 'unauthenticated';
  adobeUser: AdobeSessionUser | null;
  authExpiresAt: number | null;
  authError: string | null;
  refreshAuth: () => Promise<void>;
  loginWithAdobe: () => void;
  logoutOfAdobe: () => Promise<void>;
};

const Ctx = createContext<VipMpSessionValue | null>(null);

export function VipMpSessionProvider({ children }: { children: React.ReactNode }) {
  const [analystRole] = useState<'om' | 'bpops' | 'cam'>('om');
  const [analystName, setAnalystName] = useState(DEFAULT_PARTNER_CONTACT);
  const [analystEmail, setAnalystEmail] = useState(DEFAULT_PARTNER_EMAIL);
  const [partnerOrgName, setPartnerOrgName] = useState(() => sessionStorage.getItem('vip_mp_partner_org') || DEFAULT_PARTNER_ORG);
  const [partnerContactName, setPartnerContactName] = useState(() => sessionStorage.getItem('vip_mp_partner_name') || DEFAULT_PARTNER_CONTACT);
  const [partnerContactEmail, setPartnerContactEmail] = useState(() => sessionStorage.getItem('vip_mp_partner_email') || DEFAULT_PARTNER_EMAIL);
  const [authStatus, setAuthStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [adobeUser, setAdobeUser] = useState<AdobeSessionUser | null>(null);
  const [authExpiresAt, setAuthExpiresAt] = useState<number | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const setPartnerProfile = useCallback((org: string, name: string, email: string) => {
    sessionStorage.setItem('vip_mp_partner_org', org);
    sessionStorage.setItem('vip_mp_partner_name', name);
    sessionStorage.setItem('vip_mp_partner_email', email);
    setPartnerOrgName(org);
    setPartnerContactName(name);
    setPartnerContactEmail(email);
  }, []);

  const refreshAuth = useCallback(async () => {
    setAuthStatus('loading');
    setAuthError(null);
    try {
      const session = await getAdobeSession();
      if (!session.authenticated) {
        setAdobeUser(null);
        setAuthExpiresAt(null);
        setAuthStatus('unauthenticated');
        return;
      }

      setAdobeUser(session.user);
      setAuthExpiresAt(session.expiresAt);
      setAnalystName(session.user.name);
      setAnalystEmail(session.user.email || '');
      setAuthStatus('authenticated');
    } catch (err) {
      setAdobeUser(null);
      setAuthExpiresAt(null);
      setAuthStatus('unauthenticated');
      setAuthError(err instanceof Error ? err.message : 'Failed to load Adobe session.');
    }
  }, []);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);



  const loginWithAdobe = useCallback(() => {
    startAdobeLogin();
  }, []);

  const logoutOfAdobe = useCallback(async () => {
    sessionStorage.removeItem('vip_mp_partner_org');
    sessionStorage.removeItem('vip_mp_partner_name');
    sessionStorage.removeItem('vip_mp_partner_email');
    try {
      const redirectTo = await logoutAdobeSession();
      setAdobeUser(null);
      setAuthExpiresAt(null);
      setAuthStatus('unauthenticated');
      setAnalystName(DEFAULT_PARTNER_CONTACT);
      setAnalystEmail('');
      setPartnerOrgName(DEFAULT_PARTNER_ORG);
      setPartnerContactName(DEFAULT_PARTNER_CONTACT);
      setPartnerContactEmail(DEFAULT_PARTNER_EMAIL);
      window.location.assign(redirectTo);
    } catch (err) {
      setAdobeUser(null);
      setAuthExpiresAt(null);
      setAuthStatus('unauthenticated');
      setAnalystName(DEFAULT_PARTNER_CONTACT);
      setAnalystEmail('');
      window.location.assign(window.location.origin);
    }
  }, []);

  const value = useMemo(
    () => ({
      analystRole,
      analystName,
      analystEmail,
      partnerOrgName,
      partnerContactName,
      partnerContactEmail,
      setPartnerProfile,
      authStatus,
      adobeUser,
      authExpiresAt,
      authError,
      refreshAuth,
      loginWithAdobe,
      logoutOfAdobe
    }),
    [
      adobeUser,
      analystEmail,
      analystName,
      analystRole,
      authError,
      authExpiresAt,
      authStatus,
      loginWithAdobe,
      logoutOfAdobe,
      partnerContactEmail,
      partnerContactName,
      partnerOrgName,
      refreshAuth,
      setPartnerProfile
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVipMpSession(): VipMpSessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useVipMpSession must be used within VipMpSessionProvider');
  return v;
}
